import React, { useState, useRef, useEffect } from 'react';
import { ProjectInfo, CompanyConfig, GlobalUser } from '../src/types';
import { useMsal } from '@azure/msal-react';

interface ProjectSelectionProps {
    projects: (ProjectInfo & { id: string })[];
    onSelectProject: (projectId: string) => void;
    onCreateProject: (project: ProjectInfo & { id: string }) => void;
    companyConfig: CompanyConfig;
    onUpdateCompanyConfig: (config: CompanyConfig) => void;
    currentUser: GlobalUser;
    onOpenAdmin: () => void;
}

const ProjectSelection: React.FC<ProjectSelectionProps> = ({ 
    projects, onSelectProject, onCreateProject, companyConfig, onUpdateCompanyConfig, currentUser, onOpenAdmin 
}) => {
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const { instance } = useMsal();
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsProfileMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const isSysAdmin = currentUser.globalRole === 'Administrador';

    const getInitials = (user: GlobalUser) => {
        if (!user || (!user.name && !user.email)) return 'U';
        if (user.name && !user.name.toLowerCase().includes('usuario invitado') && user.name !== user.email) {
            const parts = user.name.trim().split(/\s+/);
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            }
            return user.name.substring(0, 2).toUpperCase();
        }
        return user.email.substring(0, 2).toUpperCase();
    };

    const [localDefault, setLocalDefault] = React.useState<string | null>(
        localStorage.getItem('defaultProject_' + currentUser.id) || currentUser.defaultProjectId || null
    );

    const handleSetDefaultProject = (projectId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        localStorage.setItem('defaultProject_' + currentUser.id, projectId);
        setLocalDefault(projectId);
    };

    // Filters projects based on the current user's role and status
    const visibleProjects = projects.filter(p => p.status !== 'Inactive' && (isSysAdmin || currentUser.assignedProjects.some(ap => ap.projectId === p.id)));

    return (
        <div className="h-screen w-full bg-slate-50 dark:bg-[#0d131b] flex flex-col font-display overflow-y-auto overflow-x-hidden">
            {/* Header / Company Branding */}
            <header className="w-full bg-white dark:bg-[#1a2634] border-b border-slate-200 dark:border-slate-800 p-4 sm:p-6 flex justify-between items-center z-10">
                <div className="flex items-center gap-3 sm:gap-5">
                    <div className="flex items-center justify-center shrink-0">
                        <img
                            src={companyConfig?.logo || '/assets/logo_ld.png'}
                            alt={companyConfig?.name || 'Constructora LD'}
                            className="hidden md:block h-[52px] sm:h-[64px] max-w-[110px] sm:max-w-[140px] w-auto object-contain shrink-0"
                        />
                        <img
                            src="/assets/play_store_icon.png"
                            alt="K-LyDapp Icon"
                            className="md:hidden h-[40px] w-auto object-contain shrink-0 rounded-xl"
                        />
                    </div>
                    <div className="flex flex-col min-w-0 justify-center">
                        <h1 className="text-xl sm:text-2xl font-black text-slate-800 dark:text-white tracking-tight leading-none truncate">K-LyDapp</h1>
                        <h2 className="text-[10px] sm:text-[11px] font-bold text-primary tracking-[0.2em] mt-1 sm:mt-1.5 truncate">{companyConfig.name || 'Empresa Holding'}</h2>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.1em] mt-1 pr-2 hidden sm:block">Plataforma para el Control de Obras</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    {isSysAdmin && (
                        <>
                            <button 
                                onClick={() => setIsConfigModalOpen(true)}
                                className="p-2 text-slate-400 hover:text-primary transition-colors hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg"
                                title="Configuración de Empresa"
                            >
                                <span className="material-symbols-outlined text-[24px]">settings</span>
                            </button>
                            <div className="h-8 w-[1px] bg-slate-200 dark:bg-slate-700 mx-1 hidden sm:block" />
                        </>
                    )}
                    
                    <div className="text-right hidden sm:block mr-2">
                        <p className="text-xs font-bold text-slate-400 uppercase">{isSysAdmin ? 'Administrador' : 'Usuario'}</p>
                        <p className="text-sm font-bold text-slate-700 dark:text-slate-200">{currentUser.name}</p>
                    </div>
                    <div className="relative" ref={menuRef}>
                        <div 
                            onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                            className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center border border-primary/20 text-sm font-bold uppercase tracking-widest overflow-hidden ring-2 ring-transparent hover:ring-primary/30 cursor-pointer transition-all"
                        >
                            {getInitials(currentUser)}
                        </div>
                        
                        {isProfileMenuOpen && (
                            <div className="absolute top-full right-0 mt-3 w-56 bg-white dark:bg-[#1a2634] rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 py-2 z-50 animate-scale-up origin-top-right">
                                <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 mb-2">
                                    <p className="text-xs font-bold text-slate-400 uppercase mb-1">Conectado como</p>
                                    <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{currentUser.email}</p>
                                </div>
                                <button
                                    onClick={() => instance.logoutRedirect()}
                                    className="w-full text-left px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 flex items-center gap-2 transition-colors"
                                >
                                    <span className="material-symbols-outlined text-[18px]">logout</span>
                                    Cerrar Sesión
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <main className="flex-1 max-w-7xl w-full mx-auto p-4 md:p-8 space-y-8 md:space-y-12 pb-36 md:pb-40 relative">
                {/* Hero / Info Section */}
                <section className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white dark:bg-[#1a2634] p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-primary">
                            <span className="material-symbols-outlined text-3xl">precision_manufacturing</span>
                        </div>
                        <div>
                            <p className="text-2xl font-black text-slate-800 dark:text-white">{projects.length}</p>
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Proyectos Activos</p>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-[#1a2634] p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-green-50 dark:bg-green-900/20 flex items-center justify-center text-green-600">
                            <span className="material-symbols-outlined text-3xl">assignment_turned_in</span>
                        </div>
                        <div>
                            <p className="text-2xl font-black text-slate-800 dark:text-white">84%</p>
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Cumplimiento General</p>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-[#1a2634] p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center text-amber-600">
                            <span className="material-symbols-outlined text-3xl">warning</span>
                        </div>
                        <div>
                            <p className="text-2xl font-black text-slate-800 dark:text-white">12</p>
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Alertas Pendientes</p>
                        </div>
                    </div>
                </section>

                {/* Projects Section */}
                <section className="space-y-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-end border-b border-slate-200 dark:border-slate-800 pb-3 gap-3">
                        <div>
                            <h2 className="text-base font-black text-slate-800 dark:text-white tracking-tight">Bienvenido a la plataforma de Control de Obras</h2>
                            <p className="text-slate-500 text-xs">{isSysAdmin ? 'Visualiza y opera en las obras de la empresa' : 'Estos son tus proyectos asignados.'}</p>
                        </div>
                        {isSysAdmin && (
                            <button
                                onClick={onOpenAdmin}
                                className="flex items-center justify-center gap-1.5 px-4 py-2 bg-slate-900 hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all border border-slate-700 w-full sm:w-auto"
                            >
                                <span className="material-symbols-outlined text-[18px] text-primary-200">admin_panel_settings</span>
                                <span className="font-bold text-xs tracking-wide">Administración General</span>
                            </button>
                        )}
                    </div>

                    {localDefault && visibleProjects.find(p => p.id === localDefault) && (
                        <>
                            {/* MOBILE: Default Project Mini Widget */}
                            <div className="mb-6 md:hidden">
                                {visibleProjects.filter(p => p.id === localDefault).map(p => (
                                    <button
                                        key={p.id}
                                        onClick={() => onSelectProject(p.id)}
                                        className="group w-full bg-blue-50 dark:bg-blue-900/10 border border-primary/50 dark:border-primary/30 rounded-xl p-3 text-left hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-all shadow-sm hover:shadow-md relative overflow-hidden"
                                    >
                                        <div className="flex gap-2.5">
                                            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center z-10 shadow-sm shrink-0 overflow-hidden p-0.5 border border-primary/20">
                                                <img src={'/assets/play_store_icon.png'} alt="Icono K" className="w-full h-full object-contain rounded" />
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-1 text-amber-500 font-bold text-[9px] tracking-widest uppercase">
                                                    <span className="material-symbols-outlined text-[10px]">star</span>
                                                    Predeterminada
                                                </div>
                                                <div className="flex items-center gap-1 bg-primary text-white px-1.5 py-0.5 rounded mr-auto w-fit text-[9px] font-black uppercase tracking-wider shadow-sm">
                                                    Ingresar
                                                    <span className="material-symbols-outlined text-[10px]">arrow_forward</span>
                                                </div>
                                            </div>
                                        </div>
                                        <h3 className="text-sm leading-tight font-black text-slate-900 dark:text-white mt-2">{p.name}</h3>
                                    </button>
                                ))}
                            </div>

                            {/* DESKTOP: Default Project Big Widget */}
                            <div className="mb-10 hidden md:block">
                                <h3 className="text-sm font-black text-primary tracking-widest mb-4 flex items-center gap-2">
                                    <span className="material-symbols-outlined md-18">star</span>
                                    Obra Predeterminada
                                </h3>
                                {visibleProjects.filter(p => p.id === localDefault).map(p => (
                                    <button
                                        key={p.id}
                                        onClick={() => onSelectProject(p.id)}
                                        className="group w-full md:w-2/3 bg-blue-50 dark:bg-blue-900/10 border-2 border-primary rounded-3xl p-8 text-left hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 relative overflow-hidden"
                                    >
                                        <div className="flex justify-between items-start mb-6">
                                            <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center z-10 shadow-lg shadow-primary/30 overflow-hidden p-1.5 border border-primary/20">
                                                <img src={companyConfig?.logo || '/assets/logo_ld.png'} alt="Logo" className="hidden md:block w-full h-full object-contain" />
                                                <img src="/assets/play_store_icon.png" alt="K-LyDapp" className="block md:hidden w-full h-full object-contain rounded-xl" />
                                            </div>
                                            <span className="px-4 py-1.5 bg-white dark:bg-slate-800 text-primary rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm">Ingreso Rápido</span>
                                        </div>
                                        <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2">{p.name}</h3>
                                        <div className="flex items-center gap-2 text-primary font-black uppercase tracking-wider group-hover:gap-4 transition-all z-10 mt-6">
                                            Entrar a Obra Predeterminada
                                            <span className="material-symbols-outlined">arrow_forward</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </>
                    )}

                    <div>
                        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4">
                            {localDefault ? 'Otras obras asignadas' : 'Obras asignadas'}
                        </h3>
                        {/* MOBILE: Renderizadas como lista (Oculta en Desktop) */}
                        <div className="md:hidden flex flex-col gap-2 max-w-2xl">
                            {visibleProjects.filter(p => p.id !== localDefault).map(p => (
                                <div
                                    key={p.id}
                                    onClick={() => onSelectProject(p.id)}
                                    className="group bg-white dark:bg-[#1a2634] border border-slate-200 dark:border-slate-800 rounded-lg p-2 text-left hover:border-primary transition-all shadow-sm hover:shadow-md cursor-pointer flex flex-col items-start relative overflow-hidden gap-1.5"
                                >
                                    {!isSysAdmin && <div className="absolute top-0 right-0 w-16 h-16 bg-primary/5 rounded-bl-full -z-10 transition-transform group-hover:scale-110" />}
                                    
                                    <div className="flex items-center gap-2.5 w-full">
                                        <div className="w-7 h-7 rounded-md bg-white flex items-center justify-center z-10 shrink-0 overflow-hidden p-0.5 border border-slate-200 dark:border-slate-700">
                                            <img src={'/assets/play_store_icon.png'} alt="Icono K" className="w-full h-full object-contain rounded-sm" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-xs font-black text-slate-800 dark:text-white group-hover:text-primary transition-colors leading-tight truncate">{p.name}</h3>
                                            <div className="flex items-center">
                                                <span className="text-slate-400 group-hover:text-primary text-[8px] font-bold uppercase tracking-widest">Activa</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-1.5 w-full mt-0.5">
                                        <button 
                                            onClick={(e) => handleSetDefaultProject(p.id, e)}
                                            className="text-amber-500 hover:text-amber-600 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/10 dark:hover:bg-amber-900/30 px-1.5 py-0.5 rounded-sm text-[9px] font-black tracking-widest transition-colors flex items-center gap-1"
                                        >
                                            <span className="material-symbols-outlined text-[11px]">star</span>
                                            Predeterminar
                                        </button>

                                        <div className="flex items-center gap-1 bg-primary/10 group-hover:bg-primary text-primary group-hover:text-white px-1.5 py-0.5 rounded-sm w-fit text-[9px] font-black uppercase tracking-wider transition-all">
                                            Ingresar
                                            <span className="material-symbols-outlined text-[11px]">arrow_forward</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {visibleProjects.filter(p => !localDefault || p.id !== localDefault).length === 0 && (
                                <div className="py-6 text-slate-500 dark:text-slate-400 italic text-sm border border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-8 text-center bg-slate-50 dark:bg-slate-800/20">
                                    No tienes más obras asignadas para listar.
                                </div>
                            )}
                        </div>

                        {/* DESKTOP: Renderizado Original como Grid (Oculto en Móvil) */}
                        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {visibleProjects.filter(p => p.id !== localDefault).map(p => (
                                <button
                                    key={p.id}
                                    onClick={() => onSelectProject(p.id)}
                                    className="group bg-white dark:bg-[#1a2634] border border-slate-200 dark:border-slate-800 rounded-3xl p-8 text-left hover:border-primary transition-all hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1 relative overflow-hidden flex flex-col h-full"
                                >
                                    {!isSysAdmin && <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full -z-10 transition-transform group-hover:scale-110" />}
                                    
                                    <div className="flex justify-between items-start mb-6 w-full">
                                        <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center transition-colors z-10 shadow-sm overflow-hidden border border-slate-200 dark:border-slate-700 p-1">
                                            <img src={companyConfig?.logo || '/assets/logo_ld.png'} alt="Logo" className="hidden md:block w-full h-full object-contain" />
                                            <img src="/assets/play_store_icon.png" alt="K-LyDapp" className="block md:hidden w-full h-full object-contain rounded-lg" />
                                        </div>
                                        <div 
                                            onClick={(e) => handleSetDefaultProject(p.id, e)}
                                            title="Establecer como obra predeterminada"
                                            className="p-2 rounded-full border border-slate-200 dark:border-slate-700 text-slate-400 hover:text-amber-500 hover:border-amber-200 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all z-20 bg-white dark:bg-[#1a2634]"
                                        >
                                            <span className="material-symbols-outlined text-lg">star</span>
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-black text-slate-800 dark:text-white mb-2 group-hover:text-primary transition-colors flex-1">{p.name}</h3>
                                    
                                    <div className="flex items-center gap-2 text-primary text-sm font-black uppercase tracking-wider group-hover:gap-3 transition-all z-10 mt-8 w-full">
                                        Entrar a Obra
                                        <span className="material-symbols-outlined">arrow_forward</span>
                                    </div>
                                </button>
                            ))}
                            {visibleProjects.filter(p => !localDefault || p.id !== localDefault).length === 0 && (
                                <div className="col-span-full py-6 text-slate-500 dark:text-slate-400 italic text-sm">
                                    No hay otras obras en la lista.
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </main>

            {/* Administrador Global Button moved to header section inside page */}
            {/* Company Config Modal */}
            {isConfigModalOpen && (
                <CompanyConfigModal 
                    config={companyConfig}
                    onClose={() => setIsConfigModalOpen(false)}
                    onSave={(newConfig) => {
                        onUpdateCompanyConfig(newConfig);
                        setIsConfigModalOpen(false);
                    }}
                />
            )}


        </div>
    );
};

// --- Sub-component for Company Configuration ---
const CompanyConfigModal: React.FC<{
    config: CompanyConfig;
    onClose: () => void;
    onSave: (config: CompanyConfig) => void;
}> = ({ config, onClose, onSave }) => {
    const [tempConfig, setTempConfig] = useState(config);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setTempConfig({ ...tempConfig, logo: reader.result as string });
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-[#1a2634] rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden border border-slate-200 dark:border-slate-800 animate-scale-up">
                <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-[#1e2c3c]">
                    <div>
                        <h3 className="text-base font-black text-slate-800 dark:text-white tracking-tight uppercase">Configuración de Empresa</h3>
                        <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">Identidad de Marca</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
                        <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                </div>
                
                <div className="p-4 space-y-3 overflow-y-auto max-h-[70vh] no-scrollbar">
                    {/* Logo Section */}
                    <div className="flex flex-col items-center gap-3 py-2">
                        <div className="relative group">
                            <div className="w-24 h-24 rounded-xl bg-slate-50 dark:bg-slate-800 border-2 border-dashed border-slate-200 dark:border-slate-700 flex items-center justify-center overflow-hidden transition-all group-hover:border-primary">
                                {tempConfig.logo ? (
                                    <img src={tempConfig.logo} alt="Logo preview" className="w-full h-full object-contain p-2" />
                                ) : (
                                    <span className="material-symbols-outlined text-3xl text-slate-300">image</span>
                                )}
                            </div>
                            <button 
                                onClick={() => fileInputRef.current?.click()}
                                className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-primary text-white shadow-lg flex items-center justify-center hover:bg-blue-600 transition-all border-4 border-white dark:border-[#1a2634]"
                            >
                                <span className="material-symbols-outlined text-[16px]">photo_camera</span>
                            </button>
                            <input 
                                type="file" 
                                ref={fileInputRef} 
                                onChange={handleFileChange} 
                                className="hidden" 
                                accept="image/*"
                            />
                        </div>
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Logo de la Empresa (PNG/JPG)</p>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                        <div className="space-y-1">
                            <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">Nombre Comercial</label>
                            <input
                                type="text"
                                value={tempConfig.name}
                                onChange={e => setTempConfig({ ...tempConfig, name: e.target.value })}
                                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs focus:ring-4 focus:ring-primary/10 transition-all outline-none"
                                placeholder="Ej: Mi Constructora"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">R.U.T</label>
                                <input
                                    type="text"
                                    value={tempConfig.rut}
                                    onChange={e => setTempConfig({ ...tempConfig, rut: e.target.value })}
                                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs outline-none focus:ring-4 focus:ring-primary/10 transition-all"
                                    placeholder="00.000.000-0"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">Razón Social</label>
                                <input
                                    type="text"
                                    value={tempConfig.razonSocial}
                                    onChange={e => setTempConfig({ ...tempConfig, razonSocial: e.target.value })}
                                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs outline-none focus:ring-4 focus:ring-primary/10 transition-all"
                                    placeholder="Ej: Constructora S.A."
                                />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">Dirección</label>
                            <input
                                type="text"
                                value={tempConfig.direccion}
                                onChange={e => setTempConfig({ ...tempConfig, direccion: e.target.value })}
                                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs outline-none focus:ring-4 focus:ring-primary/10 transition-all"
                                placeholder="Ej: Av. Principal #123"
                            />
                        </div>
                    </div>

                    <button
                        onClick={() => onSave(tempConfig)}
                        disabled={!tempConfig.name}
                        className="w-full py-3 bg-primary text-white rounded-xl text-xs font-black tracking-widest uppercase hover:bg-blue-600 transition-all shadow-xl shadow-blue-500/20 disabled:opacity-50 disabled:shadow-none hover:-translate-y-0.5 mt-2"
                    >
                        Guardar Cambios
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProjectSelection;
